import { useCallback, useContext, useMemo } from 'react'
import { useTranslate } from 'react-polyglot'
import {
  AddOnFilledIcon,
  AddOnOutlinedIcon,
  AddressBookFilledIcon,
  AddressBookOutlinedIcon,
  AttachmentFilledIcon,
  AttachmentOutlinedIcon,
  BookOutlinedIcon,
  CalendarFilledIcon,
  CalendarOutlinedIcon,
  CashOutlinedIcon,
  CloudOutlinedIcon,
  ContactAddressBookFilledIcon,
  CrowdFilledIcon,
  CrowdOutlinedIcon,
  DashboardFilledIcon,
  DashboardOutlinedIcon,
  EducationFilledIcon,
  EducationOutlinedIcon,
  FaxOutlinedIcon,
  FileLogFilledIcon,
  FileLogOutlinedIcon,
  FilterFilledIcon,
  FilterOutlinedIcon,
  FlowchartOutlinedIcon,
  GlobeOutlinedIcon,
  GotoAssistIcon,
  HistoryOutlinedIcon,
  InfoFilledIcon,
  InfoOutlinedIcon,
  IntegrationsOutlinedIcon,
  KeyFilledIcon,
  KeyOutlinedIcon,
  LandlineFilledIcon,
  LockedFilledIcon,
  LockedOutlinedIcon,
  LoudspeakerOutlinedIcon,
  MagicWandOutlinedIcon,
  MergeOutlinedIcon,
  MessageWhisperOutlinedIcon,
  MusicFilledIcon,
  NetworkFilledIcon,
  NetworkOutlinedIcon,
  NoteOutlinedIcon,
  PaymentDueOutlinedIcon,
  PeopleFilledIcon,
  PeopleOutlinedIcon,
  PersonFilledIcon,
  PersonOutlinedIcon,
  PhoneIncomingFilledIcon,
  PhoneIncomingOutlinedIcon,
  PhoneUpFilledIcon,
  PhoneUpOutlinedIcon,
  PollsFilledIcon,
  PollsOutlinedIcon,
  PopOutOutlinedIcon,
  RemoteSupportOutlinedIcon,
  ReportOutlinedIcon,
  RoomPhysicalFilledIcon,
  SalesQuoteOutlinedIcon,
  ScreenOutlinedIcon,
  SearchOutlinedIcon,
  ShapeToolFilledIcon,
  ShapeToolOutlinedIcon,
  SpacesFilledIcon,
  SpacesOutlinedIcon,
  SubscriptionsFilledIcon,
  SubscriptionsOutlinedIcon,
  SyncOutlinedIcon,
  TableFilledIcon,
  TaxExemptionOutlinedIcon,
  TextFieldFilledIcon,
  TextFieldOutlinedIcon,
  TicketOutlinedIcon,
  VirtualReceptionistFilledIcon,
  VirtualReceptionistOutlinedIcon,
  VoicemailOutlinedIcon,
  WebcamOnFilledIcon,
  WebcamOnOutlinedIcon,
  WrenchFilledIcon,
  WrenchOutlinedIcon,
} from '@getgo/chameleon-icons/react'
import {
  ACTIVITY_ROUTE,
  ADD_ONS_ROUTE,
  BILLING_ROUTE,
  DASHBOARD_ROUTE,
  DEVICES_ROUTE,
  DIAL_PLANS_ROUTE,
  DIGITAL_WORKFORCE_ROUTE,
  GlobalSecurityContext,
  GTC_BILLING_ROUTE,
  INTEGRATIONS_ROUTE,
  isPrivilegeGranted,
  NETWORK_ROUTE,
  ORDERS_ROUTE,
  OVERVIEW_ROUTE,
  PEOPLE_ROUTE,
  PHONES_ROUTE,
  PLACES_ROUTE,
  PRODUCTS_ROUTE,
  REPORTS_ROUTE,
  SETTINGS_ROUTE,
  TENANTS_ROUTE,
} from '@ucc/unified-admin-common'

import HelplineFilledIcon from 'assets/helpline.svg'
import { ConnectedUserContext, ContactCenterUserContext, OrganizationContext } from 'components'
import { useAccountContext } from 'components/contexts/AccountContext'
import { UnleashContext } from 'components/providers/UnleashProvider'
import { config } from 'config'
import {
  useApplications,
  useTenantsApp,
  useUserHasAdminRoles,
  useUserHasLicense,
  useUserHasManagedLicense,
} from 'hooks'
import useBillingFeatures from 'hooks/useBillingFeatures'
import useFeatureFlagEnabled from 'hooks/useFeatureFlagEnabled'
import useGtcBillingAccess from 'hooks/useGtcBillingAccess'
import useIsUserSystemUser from 'hooks/useIsUserSystemUser'
import { SidebarLink } from 'types/SidebarLink'
import { getProductFamilyName } from 'utils'
import { FEATURE_FLAGS, ZERO_TRUST_ROUTE } from 'utils/constants'
import useCallQueuesLinks from '../hooks/useCallQueuesLinks'
import { useInboxLinks } from '../hooks/useInboxLinks'
import { Privileges } from './rolesToPrivilegesMapper'

export type SidebarLinks = { [key in string]: SidebarLink[] }

const AppLinks = (): SidebarLinks => {
  const t = useTranslate()
  const pbx = useContext(OrganizationContext)
  const connectedUser = useContext(ConnectedUserContext)
  const securityContext = useContext(GlobalSecurityContext)
  const { userContext } = useContext(ContactCenterUserContext)
  const { isUserSystemUser } = useIsUserSystemUser()
  const isDirectories = useFeatureFlagEnabled(FEATURE_FLAGS.directories)
  const isSipTrunksEnabled = useFeatureFlagEnabled(FEATURE_FLAGS.adminSipTrunks)
  const smsCampaignRegistration = useFeatureFlagEnabled(FEATURE_FLAGS.smsCampaignRegistration)
  const virtualReceptionist = useFeatureFlagEnabled(FEATURE_FLAGS.virtualReceptionist)
  const { gotoContactsEnabled } = useContext(UnleashContext)
  const { gotoLaunchpadEnabled } = useContext(UnleashContext)
  const applicationList = useApplications()?.items || []
  const hasCallReportApp = !!applicationList.find((app) => app.id === 'call-reports' || app.id == 'analytics')

  const userHasLicense = useUserHasLicense()
  const userHasManagedLicense = useUserHasManagedLicense()
  const {
    currentAccount: pathAccount,
    accountKey: selectedAccountKey,
    productsSettings,
    licensesDetails,
  } = useAccountContext()
  const isSettingsTemplateEnabled = pbx?.showSettingsTemplate
  const isUserOrganizationAdmin = !!connectedUser?.orgAdmin
  const doesUserHaveOrganizationId = !!connectedUser?.organizationKey
  const userHasAdminRoles = useUserHasAdminRoles()
  const showTenantsApp = useTenantsApp()
  const {
    isAccountOnCommerceSystem,
    isBillingAdmin,
    systemUserHasBillingAccess,
    partnerHasChildBillingAccess,
    showPendingRenewal,
    showPaymentMethod,
    showTaxExemption,
    showQuotes,
  } = useBillingFeatures()
  const { isGtcBillingAccount, canPayInGtcBilling, canViewGtcBilling, canViewBillingDetailsInGtcBilling } =
    useGtcBillingAccess()

  const showSmsActionBadge = useCallback(
    () =>
      (pbx?.pbxSmsCampaignDetails?.pbxIsUsingSms &&
        pbx?.pbxSmsCampaignDetails?.pbxNeedsACampaignToUseSms &&
        pbx?.pbxSmsCampaignDetails?.userIsAddOnAdmin &&
        !pbx?.pbxSmsCampaignDetails?.pbxHasACampaign) ||
      pbx?.pbxSmsCampaignDetails?.pbxSmsActionRequired ||
      pbx?.pbxSmsCampaignDetails?.pbxHasSubIdWaitingCustomerAnswer,
    [pbx?.pbxSmsCampaignDetails],
  )

  const showAddOnsPendingTransactionsBadge = useCallback(
    () => pbx?.productsExploration?.pendingTransactions && pbx?.productsExploration?.pendingTransactions.length > 0,
    [pbx?.productsExploration?.pendingTransactions],
  )
  const showPartnerSettingsLink = useMemo(() => {
    const connectedAccount = connectedUser?.accounts?.find((acc) => acc.key === selectedAccountKey)
    return (
      ((!!connectedAccount?.adminRoles?.includes('SUPER_USER') ||
        !!connectedAccount?.billingRoles?.includes('PARENT_ACCESS')) &&
        !!connectedAccount?.parentKey?.length &&
        !!connectedAccount?.roleFilterEnabled) ||
      (isUserSystemUser && pathAccount?.parentKey)
    )
  }, [connectedUser, isUserSystemUser, pathAccount, selectedAccountKey])

  const showUserSyncLink = useMemo(
    () =>
      isUserOrganizationAdmin &&
      doesUserHaveOrganizationId &&
      userHasAdminRoles('MANAGE_USERS', selectedAccountKey) &&
      userHasManagedLicense(),
    [isUserOrganizationAdmin, doesUserHaveOrganizationId, selectedAccountKey, userHasAdminRoles, userHasManagedLicense],
  )

  const showResolveAILink = useMemo(
    () =>
      licensesDetails?.results?.some(
        (license) =>
          license.products?.includes('GOTORESOLVE') && !!license.entitlements?.GOTORESOLVE?.aiTrainingProvisioned,
      ),
    [licensesDetails],
  )

  const SkeletonLink = useMemo(
    () => ({
      key: 'skeleton',
    }),
    [],
  )

  const CallQueuesLinks = useCallQueuesLinks({ userContext, SkeletonLink, t })
  const { inboxRoute, inboxLinks, filterInbox } = useInboxLinks({ SkeletonLink })

  const VirtualFaxLink: SidebarLink = {
    key: 'virtual-fax',
    to: `/${PHONES_ROUTE}/virtual-fax`,
    primary: t('Virtual fax'),
    activeIcon: <FaxOutlinedIcon />,
    inactiveIcon: <FaxOutlinedIcon />,
    privilege: Privileges.AppLinks,
    globalSearchBarDetails: [
      {
        keyword: t('Virtual fax'),
        to: `/${PHONES_ROUTE}/virtual-fax`,
        tags: [t('Extensions'), t('Email to fax'), t('Call route')],
      },
    ],
  }

  const ProductsLink: SidebarLink = {
    key: 'products',
    to: `/${PRODUCTS_ROUTE}`,
    primary: t('Products'),
    activeIcon: <SpacesFilledIcon />,
    inactiveIcon: <SpacesOutlinedIcon />,
    privilege: Privileges.ViewProductsTab,
    globalSearchBarDetails: [
      {
        keyword: t('Your products'),
        to: `/${PRODUCTS_ROUTE}`,
        tags: [t('Licenses')],
      },
    ],
  }

  const AccountSettingsLink: SidebarLink = {
    key: 'account-settings',
    to: `/${SETTINGS_ROUTE}/account`,
    primary: t('Account settings'),
    activeIcon: <InfoFilledIcon />,
    inactiveIcon: <InfoOutlinedIcon />,
    privilege: Privileges.ViewSettingsTab,
    globalSearchBarDetails: [
      { keyword: t('Account settings'), to: `/${SETTINGS_ROUTE}/account`, tags: [t('Defaults')] },
    ],
  }
  const PartnerSettingsLink: SidebarLink = {
    key: 'Partner-settings',
    to: `/${SETTINGS_ROUTE}/partner`,
    primary: t('Partner settings'),
    activeIcon: <PeopleFilledIcon />,
    inactiveIcon: <PeopleOutlinedIcon />,
    privilege: Privileges.ViewSettingsTab,
    globalSearchBarDetails: [
      { keyword: t('Partner settings'), to: `/${SETTINGS_ROUTE}/partner`, tags: [t('Partner settings')] },
    ],
  }

  const ResolveDeviceGroupsLink: SidebarLink = {
    key: 'resolve-device-groups',
    to: `/${SETTINGS_ROUTE}/resolve/device-groups`,
    primary: t('Device groups'),
    activeIcon: <RemoteSupportOutlinedIcon />,
    inactiveIcon: <InfoOutlinedIcon />,
    privilege: Privileges.ViewSettingsTab,
    globalSearchBarDetails: [
      {
        keyword: t('Device groups'),
        to: `/${SETTINGS_ROUTE}/resolve-device-groups`,
        tags: [t('Resolve settings'), t('Device groups')],
      },
    ],
  }

  const ZeroTrustLink: SidebarLink = {
    key: 'zero-trust',
    to: `/${SETTINGS_ROUTE}/${ZERO_TRUST_ROUTE}`,
    primary: t('Zero trust status'),
    activeIcon: <KeyFilledIcon />,
    inactiveIcon: <KeyOutlinedIcon />,
    privilege: Privileges.ViewSettingsTab,
    globalSearchBarDetails: [
      {
        keyword: t('Zero trust status'),
        to: `/${SETTINGS_ROUTE}/${ZERO_TRUST_ROUTE}`,
        tags: [t('Resolve settings'), t('Zero trust status')],
      },
    ],
  }

  const GroupsLink: SidebarLink = useMemo(
    () => ({
      key: 'user-groups',
      to: `/${PEOPLE_ROUTE}/user-groups`,
      primary: t('User groups'),
      activeIcon: <PeopleFilledIcon />,
      inactiveIcon: <PeopleOutlinedIcon />,
      privilege: Privileges.ViewGroupsTab,
      globalSearchBarDetails: [
        {
          keyword: t('User groups'),
          to: `/${PEOPLE_ROUTE}/user-groups`,
          tags: [t('Users'), t('Admin'), t('People')],
        },
      ],
    }),
    [t],
  )

  const PhonesSettingsLink: SidebarLink = {
    key: 'phones-settings',
    to: `/${SETTINGS_ROUTE}/phones`,
    primary: t('Phones'),
    activeIcon: <PhoneUpFilledIcon />,
    inactiveIcon: <PhoneUpOutlinedIcon />,
    privilege: Privileges.ViewSettingsPhonesTab,
    globalSearchBarDetails: [
      {
        keyword: t('Phone settings'),
        to: `/${SETTINGS_ROUTE}/phones`,
        tags: [t('GTC settings'), t('Default location')],
      },
    ],
  }

  const TrainingSettingsLink: SidebarLink = {
    key: 'training-settings',
    to: `/${SETTINGS_ROUTE}/training`,
    primary: t('Training'),
    activeIcon: <EducationFilledIcon />,
    inactiveIcon: <EducationOutlinedIcon />,
    privilege: Privileges.ViewSettingsTab,
    globalSearchBarDetails: [
      {
        keyword: t('Training settings'),
        to: `/${SETTINGS_ROUTE}/training`,
        tags: [t('GTT settings')],
      },
    ],
  }

  const WebinarSettingsLink: SidebarLink = {
    key: 'webinar-settings',
    to: `/${SETTINGS_ROUTE}/webinar`,
    primary: t('Webinars'),
    activeIcon: <LoudspeakerOutlinedIcon />,
    inactiveIcon: <LoudspeakerOutlinedIcon />,
    privilege: Privileges.ViewSettingsTab,
    globalSearchBarDetails: [
      { keyword: t('Webinar settings'), to: `/${SETTINGS_ROUTE}/webinar`, tags: [t('GTW settings')] },
    ],
  }

  const PhoneNumbersLink: SidebarLink = {
    key: 'phone-numbers',
    to: `/${PHONES_ROUTE}/phone-numbers`,
    primary: t('Phone numbers'),
    activeIcon: <PhoneUpFilledIcon />,
    inactiveIcon: <PhoneUpOutlinedIcon />,
    privilege: Privileges.AppLinks,
    globalSearchBarDetails: [
      {
        keyword: t('Phone numbers'),
        to: `/${PHONES_ROUTE}/phone-numbers`,
        suggestedScore: 4,
        tags: [t('Route'), t('Caller ID'), t('Text'), 'SMS'],
      },
    ],
  }

  const MeetingsSettingsLink: SidebarLink = {
    key: 'meetings-settings',
    to: `/${SETTINGS_ROUTE}/meetings`,
    primary: t('Meetings'),
    activeIcon: <WebcamOnFilledIcon />,
    inactiveIcon: <WebcamOnOutlinedIcon />,
    privilege: Privileges.ViewSettingsTab,
    globalSearchBarDetails: [
      {
        keyword: t('Meeting settings'),
        to: `/${SETTINGS_ROUTE}/meetings`,
        tags: [t('GTM settings')],
      },
    ],
  }

  const RoomsSettingsLink: SidebarLink = {
    key: 'rooms-settings',
    to: `/${SETTINGS_ROUTE}/rooms`,
    primary: t('Rooms'),
    activeIcon: <RoomPhysicalFilledIcon />,
    inactiveIcon: <RoomPhysicalFilledIcon />,
    privilege: Privileges.ViewSettingsTab,
    globalSearchBarDetails: [
      {
        keyword: t('Room settings'),
        to: `/${SETTINGS_ROUTE}/rooms`,
        tags: [t('GoTo rooms'), t('GTR settings')],
      },
    ],
  }

  const LocationsLink: SidebarLink = {
    key: 'locations',
    to: `/${PLACES_ROUTE}`,
    primary: t('Locations'),
    activeIcon: <AddressBookFilledIcon />,
    inactiveIcon: <AddressBookOutlinedIcon />,
    privilege: Privileges.AppLinks,
    globalSearchBarDetails: [
      {
        keyword: t('All locations'),
        to: `/${PLACES_ROUTE}`,
        tags: [t('Addresses')],
        suggestedScore: 3,
      },
      {
        keyword: t('Emergency locations'),
        to: `/${PLACES_ROUTE}/emergency-locations-overview`,
        tags: [t('E911'), t('Callback number'), '911', '112'],
      },
    ],
  }

  const SchedulesLink: SidebarLink = {
    key: 'schedules',
    to: `/${PHONES_ROUTE}/schedules`,
    primary: t('Schedules'),
    activeIcon: <CalendarFilledIcon />,
    inactiveIcon: <CalendarOutlinedIcon />,
    privilege: Privileges.AppLinks,
    globalSearchBarDetails: [
      {
        keyword: t('Schedules'),
        to: `/${PHONES_ROUTE}/schedules`,
        tags: [t('Holidays')],
      },
    ],
  }

  const DevicesLink: SidebarLink = {
    key: 'devices',
    to: `/${DEVICES_ROUTE}`,
    primary: t('Devices'),
    activeIcon: <LandlineFilledIcon />,
    inactiveIcon: <LandlineFilledIcon />,
    privilege: Privileges.AppLinks,
    globalSearchBarDetails: [
      {
        keyword: t('All devices'),
        to: `/${DEVICES_ROUTE}`,
        suggestedScore: 2,
        tags: [
          t('Hardware'),
          t('Physical'),
          'MAC',
          t('Provisioned'),
          t('Standalone device'),
          t('Button configuration'),
          t('Low usage'),
        ],
      },
      {
        keyword: t('Desk phones'),
        to: `/${DEVICES_ROUTE}/desk-phones`,
        tags: [
          t('Hardware'),
          t('Physical'),
          'MAC',
          t('Provisioned'),
          t('Standalone device'),
          t('Button configuration'),
          t('Low usage'),
        ],
      },
      {
        keyword: t('Conference phones'),
        to: `/${DEVICES_ROUTE}/conference-phones`,
        tags: [
          t('Hardware'),
          t('Physical'),
          'MAC',
          t('Provisioned'),
          t('Standalone device'),
          t('Button configuration'),
          t('Low usage'),
        ],
      },
      {
        keyword: t('GoTo rooms'),
        to: `/${DEVICES_ROUTE}/goto-rooms`,
        tags: [t('Hardware'), t('Physical'), 'MAC', t('Provisioned')],
      },
      {
        keyword: t('Business continuity'),
        to: `/${DEVICES_ROUTE}/business-continuity`,
        tags: [
          t('Hardware'),
          t('Physical'),
          'MAC',
          t('Provisioned'),
          t('Failover'),
          t('Associated'),
          t('Association'),
          t('Protected'),
          t('Protection'),
          'BC',
          'JBC',
        ],
      },
      {
        keyword: t('Miscellaneous'),
        to: `/${DEVICES_ROUTE}/misc`,
        tags: [
          t('Hardware'),
          t('Physical'),
          'MAC',
          t('Provisioned'),
          t('Standalone device'),
          t('Button configuration'),
          t('Low usage'),
        ],
      },
    ],
  }

  const DialPlansLink: SidebarLink = {
    key: 'dial-plans',
    to: `/${DIAL_PLANS_ROUTE}`,
    primary: t('Dial plans'),
    activeIcon: <FlowchartOutlinedIcon />,
    inactiveIcon: <FlowchartOutlinedIcon />,
    privilege: Privileges.AppLinks,
    globalSearchBarDetails: [
      {
        keyword: t('Dial plans'),
        to: `/${DIAL_PLANS_ROUTE}`,
        suggestedScore: 5,
        tags: [
          t('Call flow editor'),
          t('Drag and drop'),
          t('Nodes'),
          t('Auto-attendant'),
          t('Schedules'),
          t('Extensions'),
          t('Call route'),
        ],
      },
    ],
  }

  const DashboardLink: SidebarLink = {
    key: 'dashboard',
    to: `/${DASHBOARD_ROUTE}`,
    primary: t('System health'),
    activeIcon: <DashboardFilledIcon />,
    inactiveIcon: <DashboardOutlinedIcon />,
    privilege: Privileges.AppLinks,
    globalSearchBarDetails: [
      {
        keyword: t('System health'),
        to: `/${DASHBOARD_ROUTE}`,
        tags: [
          t('Audio quality'),
          t('Audio connection'),
          t('Call volume'),
          t('Latency'),
          t('Packets'),
          t('Delay'),
          t('Loss'),
          t('Connection'),
          t('Connectivity'),
          'MOS',
          t('Watchlist'),
          t('Alerts'),
          t('Threshold'),
          t('Sensitivity'),
        ],
      },
    ],
  }

  const IntegrationsLink: SidebarLink = {
    key: 'integrations',
    to: `/${INTEGRATIONS_ROUTE}`,
    primary: t('Integrations'),
    activeIcon: <IntegrationsOutlinedIcon />,
    inactiveIcon: <IntegrationsOutlinedIcon />,
    privilege: Privileges.AppLinks,
    globalSearchBarDetails: [
      {
        keyword: t('Integrations'),
        to: `/${INTEGRATIONS_ROUTE}`,
        tags: ['Dynamics', 'Zoho', 'Salesforce', 'Hubspot'],
      },
    ],
  }

  const NetworkLink: SidebarLink = {
    key: 'network',
    to: `/${NETWORK_ROUTE}`,
    primary: t('Network'),
    activeIcon: <NetworkFilledIcon />,
    inactiveIcon: <NetworkOutlinedIcon />,
    privilege: Privileges.AppLinks,
    globalSearchBarDetails: [
      {
        keyword: t('Network test'),
        to: `/${NETWORK_ROUTE}`,
        tags: [t('Probes'), t('System code'), t('Passphrase')],
      },
    ],
  }

  const CallFiltersLink: SidebarLink = {
    key: 'call-filters',
    to: `/${PHONES_ROUTE}/call-filters`,
    primary: t('Call filters'),
    activeIcon: <FilterFilledIcon />,
    inactiveIcon: <FilterOutlinedIcon />,
    privilege: Privileges.AppLinks,
    globalSearchBarDetails: [
      { keyword: t('Call filters'), to: `/${PHONES_ROUTE}/call-filters`, tags: [t('Block calls')] },
    ],
  }

  const ExtensionsLink: SidebarLink = {
    key: 'extensions',
    to: `/${PHONES_ROUTE}/extensions`,
    primary: t('Extensions overview'),
    activeIcon: <SearchOutlinedIcon />,
    inactiveIcon: <SearchOutlinedIcon />,
    privilege: Privileges.AppLinks,
    globalSearchBarDetails: [
      {
        keyword: t('Extensions overview'),
        to: `/${PHONES_ROUTE}/extensions`,
        tags: [
          t('Ring group'),
          t('Lines'),
          t('Direct extensions'),
          t('Dial plans'),
          t('Virtual fax'),
          t('Shared voicemail'),
          t('Conference bridge'),
          t('Call route'),
        ],
      },
    ],
  }

  const getCallReportsRedirect = (): string => {
    const hasAnalytics = !!applicationList.find((app) => app.id == 'analytics')
    const baseUrl = hasAnalytics ? config.ANALYTICS_CALL_REPORTS : config.CALL_REPORTS
    return `${baseUrl}/${pbx?.domain || ''}`
  }
  const CallReportsLink: SidebarLink = {
    key: 'call-reports',
    to: getCallReportsRedirect(),
    primary: t('Call reports'),
    activeIcon: <ReportOutlinedIcon />,
    inactiveIcon: <ReportOutlinedIcon />,
    external: true,
    privilege: Privileges.AppLinks,
    globalSearchBarDetails: [
      {
        keyword: t('Call reports'),
        to: `${getCallReportsRedirect() || ''}`,
        tags: [t('Voice reports')],
      },
    ],
  }

  const SharedVoicemailLink: SidebarLink = {
    key: 'shared-voicemail',
    to: `/${PHONES_ROUTE}/shared-voicemail`,
    primary: t('Shared voicemail'),
    activeIcon: <VoicemailOutlinedIcon />,
    inactiveIcon: <VoicemailOutlinedIcon />,
    privilege: Privileges.AppLinks,
    globalSearchBarDetails: [
      {
        keyword: t('Shared voicemail'),
        to: `/${PHONES_ROUTE}/shared-voicemail`,
        tags: [t('Extensions'), t('Call route')],
      },
    ],
  }

  const OrdersLink: SidebarLink = {
    key: 'orders',
    to: `/${ORDERS_ROUTE}`,
    primary: t('Phone system orders'),
    activeIcon: <FileLogFilledIcon />,
    inactiveIcon: <FileLogOutlinedIcon />,
    privilege: Privileges.AppLinks,
    globalSearchBarDetails: [
      { keyword: t('All orders'), to: `/${ORDERS_ROUTE}`, tags: [t('Phone numbers')] },
      {
        keyword: t('Orders - In progress'),
        to: `/${ORDERS_ROUTE}?state=OPEN`,
        tags: [t('Phone numbers')],
      },
      {
        keyword: t('Orders - Completed'),
        to: `/${ORDERS_ROUTE}?state=COMPLETED`,
        tags: [t('Phone numbers')],
      },
      {
        keyword: t('Orders - Canceled'),
        to: `/${ORDERS_ROUTE}?state=CANCELED`,
        tags: [t('Phone numbers')],
      },
      {
        keyword: t('Orders - Drafts'),
        to: `/${ORDERS_ROUTE}?state=DRAFTS`,
        tags: [t('Saved ports'), t('Saved transfers')],
      },
      { keyword: t('Order new numbers'), to: '/numbers/order', tags: [t('Phone numbers')] },
      {
        keyword: t('Port existing numbers'),
        to: '/numbers/port',
        tags: [t('Transfer phone number'), t('Porting')],
      },
    ],
  }

  const AddOnsLink: SidebarLink = {
    badge: showAddOnsPendingTransactionsBadge() ? '!' : undefined,
    badgeTooltip: showAddOnsPendingTransactionsBadge()
      ? t(`We're provisioning your recent purchase |||| We're provisioning your recent purchases`, {
          smart_count:
            pbx?.productsExploration?.pendingTransactions && pbx?.productsExploration?.pendingTransactions.length,
        })
      : undefined,
    key: 'addons',
    to: `/${ADD_ONS_ROUTE}`,
    primary: t('Add-ons'),
    activeIcon: <AddOnFilledIcon />,
    inactiveIcon: <AddOnOutlinedIcon />,
    privilege: Privileges.AppLinks,
    globalSearchBarDetails: [
      {
        keyword: t('Add-ons'),
        to: `/${ADD_ONS_ROUTE}`,
        tags: [
          t('Catalog'),
          t('Catalogue'),
          t('Reporting'),
          t('Integrations'),
          t('Attendant console'),
          t('AI optimization'),
          t('Features'),
        ],
      },
    ],
  }

  const ReportsLink: SidebarLink = {
    key: 'reports',
    to: `/${REPORTS_ROUTE}`,
    primary: t('Reports'),
    activeIcon: <ReportOutlinedIcon />,
    inactiveIcon: <ReportOutlinedIcon />,
    privilege: Privileges.ViewReportsTab,
    globalSearchBarDetails: [{ keyword: t('Reports'), to: `/${REPORTS_ROUTE}` }],
  }

  const ActivityLink: SidebarLink = {
    key: 'activity',
    to: `/${ACTIVITY_ROUTE}`,
    primary: t('Activity'),
    activeIcon: <HistoryOutlinedIcon />,
    inactiveIcon: <HistoryOutlinedIcon />,
    privilege: Privileges.ViewActivityTab,
    globalSearchBarDetails: [
      {
        keyword: t('Activity'),
        to: `/${ACTIVITY_ROUTE}`,
        tags: [t('Logs'), t('Tracking'), t('History')],
      },
    ],
  }

  const ConferenceBridgesLink: SidebarLink = {
    key: 'conference-bridges',
    to: `/${PHONES_ROUTE}/conference-bridges`,
    primary: t('Conference bridges'),
    activeIcon: <RoomPhysicalFilledIcon />,
    inactiveIcon: <RoomPhysicalFilledIcon />,
    privilege: Privileges.AppLinks,
    globalSearchBarDetails: [
      {
        keyword: t('Conference bridges'),
        to: `/${PHONES_ROUTE}/conference-bridges`,
        tags: [t('Extensions'), t('Call route')],
      },
    ],
  }

  const AIRGettingStartedLink: SidebarLink = {
    key: 'air-getting-started',
    to: `/${DIGITAL_WORKFORCE_ROUTE}/getting-started`,
    primary: t('Getting started guide'),
    activeIcon: <ShapeToolFilledIcon />,
    inactiveIcon: <ShapeToolOutlinedIcon />,
    privilege: Privileges.AppLinks,
    globalSearchBarDetails: [
      {
        keyword: t('Getting started guide'),
        to: `/${DIGITAL_WORKFORCE_ROUTE}/getting-started`,
        tags: [t('Getting started guide'), t('AI'), t('Receptionists')],
      },
    ],
  }

  const KnowledgeBaseLink: SidebarLink = {
    key: 'knowledge',
    to: `/${DIGITAL_WORKFORCE_ROUTE}/knowledge`,
    primary: t('Knowledge'),
    activeIcon: <AttachmentFilledIcon />,
    inactiveIcon: <AttachmentOutlinedIcon />,
    privilege: Privileges.AppLinks,
    globalSearchBarDetails: [
      {
        keyword: t('Knowledge'),
        to: `/${DIGITAL_WORKFORCE_ROUTE}/knowledge`,
        tags: [t('Knowledge'), t('AI')],
      },
    ],
  }

  const AIReceptionistLink: SidebarLink = {
    key: 'ai-receptionists',
    to: `/${DIGITAL_WORKFORCE_ROUTE}/ai-receptionists`,
    primary: t('AI receptionists'),
    activeIcon: <VirtualReceptionistFilledIcon />,
    inactiveIcon: <VirtualReceptionistOutlinedIcon />,
    privilege: Privileges.AppLinks,
    globalSearchBarDetails: [
      {
        keyword: t('AI receptionists'),
        to: `/${DIGITAL_WORKFORCE_ROUTE}/ai-receptionists`,
        tags: [t('Extensions'), t('Call route'), t('AI'), t('Receptionists')],
      },
    ],
  }

  const InfoCaptureLink: SidebarLink = {
    key: 'info-capture',
    to: `/${DIGITAL_WORKFORCE_ROUTE}/info-capture`,
    primary: t('Info capture'),
    activeIcon: <PollsFilledIcon />,
    inactiveIcon: <PollsOutlinedIcon />,
    privilege: Privileges.AppLinks,
    globalSearchBarDetails: [
      {
        keyword: t('Info capture'),
        to: `/${DIGITAL_WORKFORCE_ROUTE}/info-capture`,
        tags: [t('Info capture'), t('Forms'), t('AI'), t('Receptionists')],
      },
    ],
  }

  const RingGroupsLink: SidebarLink = {
    key: 'ring-groups',
    to: `/${PHONES_ROUTE}/ring-groups`,
    primary: t('Ring groups'),
    activeIcon: <MergeOutlinedIcon />,
    inactiveIcon: <MergeOutlinedIcon />,
    privilege: Privileges.AppLinks,
    globalSearchBarDetails: [
      {
        keyword: t('Ring groups'),
        to: `/${PHONES_ROUTE}/ring-groups`,
        tags: [t('Extensions'), t('Call route')],
      },
    ],
  }

  const OutboundProxiesLink: SidebarLink = {
    key: 'outbound-proxies',
    to: `/${PHONES_ROUTE}/outbound-proxies`,
    primary: t('Outbound proxies'),
    activeIcon: <PopOutOutlinedIcon />,
    inactiveIcon: <PopOutOutlinedIcon />,
    privilege: Privileges.AppLinks,
    globalSearchBarDetails: [
      {
        keyword: t('Outbound proxies'),
        to: `/${PHONES_ROUTE}/outbound-proxies`,
        tags: [t('Protocol'), 'UDP', 'TCP', 'TLS'],
      },
    ],
  }

  const SipTrunksLink: SidebarLink = {
    key: 'sip-trunks',
    to: `/${PHONES_ROUTE}/sip-trunks`,
    primary: t('SIP trunks'),
    activeIcon: <CloudOutlinedIcon />,
    inactiveIcon: <CloudOutlinedIcon />,
    privilege: Privileges.AppLinks,
    globalSearchBarDetails: [
      {
        keyword: t('SIP trunks'),
        to: `/${PHONES_ROUTE}/sip-trunks`,
        tags: [t('SIP trunks')],
      },
    ],
  }

  const AudioLibraryLink: SidebarLink = {
    key: 'audio-library',
    to: `/${PHONES_ROUTE}/audio-library`,
    primary: t('Audio library'),
    activeIcon: <MusicFilledIcon />,
    inactiveIcon: <MusicFilledIcon />,
    privilege: Privileges.AppLinks,
    globalSearchBarDetails: [
      {
        keyword: t('Audio library'),
        to: `/${PHONES_ROUTE}/audio-library/sound-clips`,
        tags: [t('Sound clips'), t('Media'), t('Greetings'), t('Record'), t('Upload')],
      },
      {
        keyword: t('Hold music - System'),
        to: `/${PHONES_ROUTE}/audio-library/hold-music/built-in`,
        tags: [t('Sound clips'), t('Media'), t('Greetings')],
      },
      {
        keyword: t('Hold music - Uploaded'),
        to: `/${PHONES_ROUTE}/audio-library/hold-music/uploaded`,
        tags: [t('Sound clips'), t('Media'), t('Greetings'), t('Record'), t('Upload')],
      },
      {
        keyword: t('Playlists'),
        to: `/${PHONES_ROUTE}/audio-library/playlists`,
        tags: [t('Sound clips'), t('Media'), t('Greetings')],
      },
    ],
  }

  const DirectoriesLink: SidebarLink = {
    key: 'directories',
    to: `/${PHONES_ROUTE}/directories`,
    primary: t('Directories'),
    activeIcon: <ContactAddressBookFilledIcon />,
    inactiveIcon: <ContactAddressBookFilledIcon />,
    privilege: Privileges.AppLinks,
    globalSearchBarDetails: [
      {
        keyword: t('Directories'),
        to: `/${PHONES_ROUTE}/directories`,
        tags: [t('Dial by name'), t('Corporate')],
      },
    ],
  }

  const PagingLink: SidebarLink = {
    key: 'paging',
    to: `/${PHONES_ROUTE}/paging`,
    primary: t('Paging'),
    activeIcon: <LoudspeakerOutlinedIcon />,
    inactiveIcon: <LoudspeakerOutlinedIcon />,
    privilege: Privileges.AppLinks,
    globalSearchBarDetails: [
      {
        keyword: t('Paging'),
        to: `/${PHONES_ROUTE}/paging`,
        tags: [t('Paging profile'), t('Paging realm')],
      },
    ],
  }

  const NetworkAccessLink: SidebarLink = {
    key: 'network-access',
    to: `/${PHONES_ROUTE}/network-access`,
    primary: t('Network access'),
    activeIcon: <GlobeOutlinedIcon />,
    inactiveIcon: <GlobeOutlinedIcon />,
    privilege: Privileges.AppLinks,
    globalSearchBarDetails: [
      {
        keyword: t('Network access'),
        to: `/${PHONES_ROUTE}/network-access`,
        tags: ['IP'],
      },
    ],
  }

  const DirectExtensionsLink: SidebarLink = {
    key: 'direct-extensions',
    to: `/${PHONES_ROUTE}/direct-extensions`,
    primary: t('Direct extensions'),
    activeIcon: <PhoneIncomingFilledIcon />,
    inactiveIcon: <PhoneIncomingOutlinedIcon />,
    privilege: Privileges.AppLinks,
    globalSearchBarDetails: [
      {
        keyword: t('All direct extensions'),
        to: `/${PHONES_ROUTE}/direct-extensions/all-direct-extensions`,
        tags: [t('Lines'), t('Shared line'), t('Call route')],
      },
      {
        keyword: t('Direct extensions assigned as primary'),
        to: `/${PHONES_ROUTE}/direct-extensions/assigned-as-primary`,
        tags: [t('Lines'), t('Shared line'), t('Call route')],
      },
    ],
  }

  const TenantsLink: SidebarLink = useMemo(
    () => ({
      key: 'tenants',
      to: `/${TENANTS_ROUTE}`,
      primary: t('Tenants'),
      activeIcon: <CrowdFilledIcon />,
      inactiveIcon: <CrowdOutlinedIcon />,
      privilege: Privileges.ViewRoleAndPermissionsTab,
      globalSearchBarDetails: [
        {
          keyword: t('Tenants'),
          to: `/${TENANTS_ROUTE}`,
          tags: [t('Tenants')],
        },
      ],
    }),
    [t],
  )

  const RolesAndPermissionsLink: SidebarLink = useMemo(
    () => ({
      key: 'roles-and-permissions',
      to: `/${PEOPLE_ROUTE}/roles-and-permissions`,
      primary: t('Roles & permissions'),
      activeIcon: <LockedFilledIcon />,
      inactiveIcon: <LockedOutlinedIcon />,
      privilege: Privileges.ViewRoleAndPermissionsTab,
      globalSearchBarDetails: [
        {
          keyword: t('Roles & permissions'),
          to: `/${PEOPLE_ROUTE}/roles-and-permissions`,
          tags: [t('Users'), t('Admin'), t('People'), t('Super admin')],
        },
      ],
    }),
    [t],
  )

  const ContactsLink: SidebarLink = useMemo(
    () => ({
      key: 'contacts',
      to: `/contacts/all-contacts`,
      primary: t('Contacts'),
      activeIcon: <PeopleFilledIcon />,
      inactiveIcon: <PeopleOutlinedIcon />,
      privilege: Privileges.AppLinks,
    }),
    [t],
  )

  const ContactGroupsLink: SidebarLink = useMemo(
    () => ({
      key: 'contact-groups',
      to: `/contacts/group`,
      primary: t('Contact groups'),
      activeIcon: <CrowdFilledIcon />,
      inactiveIcon: <CrowdOutlinedIcon />,
      privilege: Privileges.AppLinks,
    }),
    [t],
  )

  const ContactCustomFieldsLink: SidebarLink = useMemo(
    () => ({
      key: 'custom-fields',
      to: `/contacts/custom-fields`,
      primary: t('Custom fields'),
      activeIcon: <TextFieldFilledIcon />,
      inactiveIcon: <TextFieldOutlinedIcon />,
      privilege: Privileges.AppLinks,
    }),
    [t],
  )

  const UsersLink: SidebarLink = useMemo(
    () => ({
      key: 'users',
      to: `/${PEOPLE_ROUTE}/users`,
      primary: t('Users'),
      activeIcon: <PersonFilledIcon />,
      inactiveIcon: <PersonOutlinedIcon />,
      privilege: Privileges.ViewUsersTab,
      globalSearchBarDetails: [
        {
          keyword: t('Users'),
          to: `/${PEOPLE_ROUTE}/users`,
          suggestedScore: 1,
          tags: [
            t('Users'),
            t('Admin'),
            t('People'),
            t('Softphones'),
            t('Licenses'),
            t('License provisioning'),
            t('Members'),
            t('Dialing permissions'),
            t('Dialable username'),
            t('Dialable password'),
          ],
        },
      ],
    }),
    [t],
  )

  const UserSyncLink: SidebarLink = useMemo(
    () => ({
      key: 'user-sync',
      to: `/${PEOPLE_ROUTE}/user-sync`,
      primary: t('User sync'),
      activeIcon: <SyncOutlinedIcon />,
      inactiveIcon: <SyncOutlinedIcon />,
      privilege: Privileges.ViewUserSyncTab,
      globalSearchBarDetails: [
        {
          keyword: t('User sync'),
          to: `/${PEOPLE_ROUTE}/user-sync`,
          tags: [t('Users'), t('Sync'), t('Rules'), t('People')],
        },
      ],
    }),
    [t],
  )

  const AssistLink: SidebarLink = {
    key: 'assist',
    to: `/${SETTINGS_ROUTE}/assist`,
    primary: t('Assist'),
    activeIcon: <GotoAssistIcon />,
    inactiveIcon: <GotoAssistIcon />,
    privilege: Privileges.ViewSettingsAssistTab,
    globalSearchBarDetails: [
      {
        keyword: t('Assist settings'),
        to: `/${SETTINGS_ROUTE}/assist`,
        tags: [t('GTA settings')],
      },
    ],
  }

  const ResolveLink: SidebarLink = {
    key: 'resolve',
    to: `/${SETTINGS_ROUTE}/resolve/system-settings`,
    primary: t('Remote Support'),
    activeIcon: <RemoteSupportOutlinedIcon />,
    inactiveIcon: <RemoteSupportOutlinedIcon />,
    privilege: Privileges.ViewSettingsTab,
    globalSearchBarDetails: [
      {
        keyword: t('Remote support settings'),
        to: `/${SETTINGS_ROUTE}/resolve/system-settings`,
        tags: [t('GTA settings')],
      },
    ],
  }

  const RmmLink: SidebarLink = {
    key: 'resolve-rmm',
    to: `/${SETTINGS_ROUTE}/resolve/rmm-settings`,
    primary: t('Monitoring and management'),
    activeIcon: <ScreenOutlinedIcon />,
    inactiveIcon: <ScreenOutlinedIcon />,
    privilege: Privileges.ViewSettingsTab,
    globalSearchBarDetails: [
      {
        keyword: t('Monitoring and management'),
        to: `/${SETTINGS_ROUTE}/resolve/rmm-settings`,
        tags: [t('Monitoring and management')],
      },
    ],
  }

  const ResolveHelplineLink: SidebarLink = {
    key: 'resolve-helpline',
    to: `/${SETTINGS_ROUTE}/resolve/helpline`,
    primary: t('Helpline'),
    activeIcon: <HelplineFilledIcon />,
    inactiveIcon: <HelplineFilledIcon />,
    privilege: Privileges.ViewSettingsTab,
    globalSearchBarDetails: [
      {
        keyword: t('Helpline settings'),
        to: `/${SETTINGS_ROUTE}/resolve/helpline`,
        tags: [t('Resolve settings'), t('Helpline settings')],
      },
    ],
  }

  const ResolveKnowledgebaseLink: SidebarLink = {
    key: 'resolve-knowledgebase',
    to: `/${SETTINGS_ROUTE}/resolve/knowledgebase`,
    primary: t('Knowledge base'),
    activeIcon: <BookOutlinedIcon />,
    inactiveIcon: <BookOutlinedIcon />,
    privilege: Privileges.ViewSettingsTab,
    globalSearchBarDetails: [
      {
        keyword: t('Knowledge base settings'),
        to: `/${SETTINGS_ROUTE}/resolve/knowledgebase`,
        tags: [t('Resolve settings'), t('Knowledge base settings')],
      },
    ],
  }

  const ResolveAILink: SidebarLink = {
    key: 'resolve-ai',
    to: `/${SETTINGS_ROUTE}/resolve/ai`,
    primary: t('Artificial intelligence'),
    activeIcon: <MagicWandOutlinedIcon />,
    inactiveIcon: <MagicWandOutlinedIcon />,
    privilege: Privileges.ViewSettingsTab,
    globalSearchBarDetails: [
      {
        keyword: t('Artificial intelligence'),
        to: `/${SETTINGS_ROUTE}/resolve/ai`,
        tags: [t('Resolve settings'), t('Artificial intelligence')],
      },
    ],
  }

  const HelpdeskLink: SidebarLink = {
    key: 'helpdesk',
    to: `/${SETTINGS_ROUTE}/helpdesk`,
    primary: t('Helpdesk'),
    activeIcon: <TicketOutlinedIcon />,
    inactiveIcon: <TicketOutlinedIcon />,
    privilege: Privileges.AppLinks,
    globalSearchBarDetails: [
      {
        keyword: t('Helpdesk settings'),
        to: `/${SETTINGS_ROUTE}/helpdesk`,
      },
    ],
  }

  const SettingsTemplateLink: SidebarLink = {
    key: 'templates',
    to: `/${SETTINGS_ROUTE}/templates`,
    primary: t('Settings templates'),
    activeIcon: <WrenchFilledIcon />,
    inactiveIcon: <WrenchOutlinedIcon />,
    privilege: Privileges.ViewManageSettingsTemplatesTab,
    globalSearchBarDetails: [
      {
        keyword: t('Setting templates'),
        to: `/${SETTINGS_ROUTE}/templates`,
        tags: [t('User settings')],
      },
    ],
  }

  const QuotesLink: SidebarLink = {
    key: 'quotes',
    to: `/${BILLING_ROUTE}/quotes`,
    primary: t('Quotes'),
    activeIcon: <SalesQuoteOutlinedIcon />,
    inactiveIcon: <SalesQuoteOutlinedIcon />,
    privilege: Privileges.ViewQuotesTab,
    globalSearchBarDetails: [],
  }

  const InvoiceLink: SidebarLink = {
    key: 'invoices',
    to: `/${BILLING_ROUTE}/invoices`,
    primary: t('Invoices'),
    activeIcon: <NoteOutlinedIcon />,
    inactiveIcon: <NoteOutlinedIcon />,
    privilege: Privileges.ViewInvoicesTab,
    globalSearchBarDetails: [
      {
        keyword: t('Invoices'),
        to: `/${BILLING_ROUTE}/invoices`,
        tags: [t('Invoices')],
      },
    ],
  }

  const PendingRenewalsLink: SidebarLink = {
    key: 'payment-due',
    to: `/${BILLING_ROUTE}/payments-due`,
    primary: t('Payments due'),
    activeIcon: <PaymentDueOutlinedIcon />,
    inactiveIcon: <PaymentDueOutlinedIcon />,
    privilege: Privileges.ViewPaymentsDueTab,
    globalSearchBarDetails: [
      {
        keyword: t('Payments due'),
        to: `/${BILLING_ROUTE}/payments-due`,
        tags: [t('Payments due')],
      },
    ],
  }

  const PaymentMethodLink: SidebarLink = {
    key: 'payment-method',
    to: `/${BILLING_ROUTE}/payment-method`,
    primary: t('Payment method'),
    activeIcon: <CashOutlinedIcon />,
    inactiveIcon: <CashOutlinedIcon />,
    privilege: Privileges.ViewPaymentMethodsTab,
    globalSearchBarDetails: [
      {
        keyword: t('Payment'),
        to: `/${BILLING_ROUTE}/payment-method`,
        tags: [t('Billing'), t('Payment'), t('Credit card')],
      },
    ],
  }

  const BillingAddressesLink: SidebarLink = {
    key: 'addresses',
    to: `/${BILLING_ROUTE}/addresses`,
    primary: t('Addresses'),
    activeIcon: <AddressBookOutlinedIcon />,
    inactiveIcon: <AddressBookOutlinedIcon />,
    privilege: Privileges.ViewAddressBookTab,
    globalSearchBarDetails: [
      {
        keyword: t('Addresses'),
        to: `/${BILLING_ROUTE}/addresses`,
        tags: [t('Billing'), t('Addresses')],
      },
    ],
  }

  const TaxExemptionLink: SidebarLink = {
    key: 'tax-exemption',
    to: `/${BILLING_ROUTE}/tax-exemption`,
    primary: t('Tax exemption'),
    activeIcon: <TaxExemptionOutlinedIcon />,
    inactiveIcon: <TaxExemptionOutlinedIcon />,
    privilege: Privileges.ViewTaxExemptionTab,
    globalSearchBarDetails: [
      {
        keyword: t('Tax exemption'),
        to: `/${BILLING_ROUTE}/tax-exemption`,
        tags: [t('Billing'), t('Tax exemption')],
      },
    ],
  }

  const SubscriptionsLink: SidebarLink = {
    key: 'subscriptions',
    to: `/${BILLING_ROUTE}/subscriptions`,
    primary: t('Subscriptions'),
    activeIcon: <SubscriptionsFilledIcon />,
    inactiveIcon: <SubscriptionsOutlinedIcon />,
    privilege: Privileges.ViewSubscriptionsTab,
    globalSearchBarDetails: [
      {
        keyword: t('Subscriptions'),
        to: `/${BILLING_ROUTE}/subscriptions`,
        tags: [t('Billing'), t('Subscriptions')],
      },
    ],
  }

  const GtcInvoiceLink: SidebarLink = {
    key: 'gtc-invoices',
    to: `/${GTC_BILLING_ROUTE}/invoices`,
    primary: t('Invoices'),
    activeIcon: <NoteOutlinedIcon />,
    inactiveIcon: <NoteOutlinedIcon />,
    privilege: Privileges.ViewGtcInvoicesTab,
    globalSearchBarDetails: [
      {
        keyword: t('Invoices'),
        to: `/${GTC_BILLING_ROUTE}/invoices`,
        tags: [t('Billing'), t('Invoices')],
      },
    ],
  }

  const GtcPaymentOptionsLink: SidebarLink = {
    key: 'gtc-payment-options',
    to: `/${GTC_BILLING_ROUTE}/payment-options`,
    primary: t('Payment options'),
    activeIcon: <CashOutlinedIcon />,
    inactiveIcon: <CashOutlinedIcon />,
    privilege: Privileges.ViewGtcPaymentsOptionsTab,
    globalSearchBarDetails: [
      {
        keyword: t('Payment options'),
        to: `/${GTC_BILLING_ROUTE}/payment-options`,
        tags: [t('Billing'), t('Payment options')],
      },
    ],
  }

  const GtcBillingDetailsLink: SidebarLink = {
    key: 'gtc-billing-details',
    to: `/${GTC_BILLING_ROUTE}/billing-details`,
    primary: t('Billing details'),
    activeIcon: <TableFilledIcon />,
    inactiveIcon: <TableFilledIcon />,
    privilege: Privileges.ViewGtcBillingDetails,
    globalSearchBarDetails: [
      {
        keyword: t('Billing details'),
        to: `/${GTC_BILLING_ROUTE}/billing-details`,
        tags: [t('Billing'), t('Billing details')],
      },
    ],
  }

  const SmsCampaignLink: SidebarLink = {
    badge: showSmsActionBadge() ? '!' : undefined,
    key: 'sms-campaign',
    to: `/sms-campaign`,
    primary: t('SMS compliance'),
    activeIcon: <MessageWhisperOutlinedIcon />,
    inactiveIcon: <MessageWhisperOutlinedIcon />,
    privilege: Privileges.AppLinks,
    globalSearchBarDetails: [
      {
        keyword: t('SMS compliance'),
        to: `/sms-campaign`,
        tags: [t('Brand'), t('Campaigns'), t('The Campaign registry'), t('10DLC'), t('Business SMS')],
      },
    ],
  }

  const LaunchpadLink: SidebarLink = {
    key: 'gotoadmin-launchpad',
    to: `/${OVERVIEW_ROUTE}`,
    primary: t('Overview'),
    activeIcon: <SpacesFilledIcon />,
    inactiveIcon: <SpacesFilledIcon />,
    privilege: Privileges.ViewProductsTab,
    globalSearchBarDetails: [
      {
        keyword: t('Overview'),
        to: `/${OVERVIEW_ROUTE}`,
        tags: [t('Overview')],
      },
    ],
  }

  const homeRoute = [
    ...(!gotoLaunchpadEnabled ? [ProductsLink] : []),
    ...(gotoLaunchpadEnabled ? [LaunchpadLink] : []),
    OrdersLink,
    DashboardLink,
    connectedUser !== null ? ReportsLink : SkeletonLink,
    connectedUser !== null ? ActivityLink : SkeletonLink,
    IntegrationsLink,
    NetworkLink,
    ...(pbx?.productsExploration?.productExplorationEnabled
      ? [
          {
            key: 'home-divider-1',
            header: ' ',
            privilege: Privileges.AppLinks,
          },
          AddOnsLink,
        ]
      : []),
  ]

  const ContactsSectionTitle = {
    key: null,
    header: t('Contacts'),
    privilege: Privileges.AppLinks,
  }

  const peopleRoute = [
    showUserSyncLink,
    UsersLink,
    GroupsLink,
    ...(showTenantsApp ? [TenantsLink] : []),
    RolesAndPermissionsLink,
    ...(showUserSyncLink ? [UserSyncLink] : []),
    ...(gotoContactsEnabled ? [ContactsSectionTitle, ContactsLink, ContactGroupsLink, ContactCustomFieldsLink] : []),
  ]

  const deviceRoute = [DevicesLink]

  const placeRoute = [LocationsLink]

  const phoneRoute = [
    PhoneNumbersLink,
    ExtensionsLink,
    CallReportsLink,
    {
      key: null,
      header: t('Call routes'),
      privilege: Privileges.AppLinks,
    },
    DirectExtensionsLink,
    DialPlansLink,
    RingGroupsLink,
    VirtualFaxLink,
    SharedVoicemailLink,
    ConferenceBridgesLink,
    {
      key: null,
      header: t('Utilities'),
      privilege: Privileges.AppLinks,
    },
    CallFiltersLink,
    SchedulesLink,
    AudioLibraryLink,
    ...(isDirectories ? [DirectoriesLink] : []),
    PagingLink,
    ...(smsCampaignRegistration &&
    pbx?.pbxSmsCampaignDetails?.pbxMayNeedACampaignToUseSms &&
    pbx?.pbxSmsCampaignDetails.userIsAddOnAdmin
      ? [SmsCampaignLink]
      : []),
    {
      key: null,
      header: t('Contact center'),
      privilege: Privileges.AppLinks,
    },
    ...CallQueuesLinks,
    {
      key: null,
      header: t('Advanced'),
      privilege: Privileges.AppLinks,
    },
    NetworkAccessLink,
    OutboundProxiesLink,
    ...(isSipTrunksEnabled ? [SipTrunksLink] : []),
  ]

  const settingRoute = [
    ...(isSettingsTemplateEnabled
      ? [
          SettingsTemplateLink,
          {
            key: null,
            header: t('Capabilites'),
            privilege: Privileges.AppLinks,
          },
        ]
      : []),
    pbx?.showJive !== undefined ? PhonesSettingsLink : SkeletonLink,
    pbx?.showMeetings !== undefined ? MeetingsSettingsLink : SkeletonLink,
    pbx?.showTraining !== undefined ? TrainingSettingsLink : SkeletonLink,
    pbx?.showWebinar !== undefined ? WebinarSettingsLink : SkeletonLink,
    pbx?.showRooms !== undefined ? RoomsSettingsLink : SkeletonLink,
    pbx?.showAssist !== undefined ? AssistLink : SkeletonLink,
    pbx?.showResolve !== undefined ? ResolveLink : SkeletonLink,
    pbx?.showCustomerEngagement !== undefined ? inboxLinks.CustomerEngagementSettingsLink : SkeletonLink,
    pbx?.showHelpdesk !== undefined ? HelpdeskLink : SkeletonLink,
    {
      key: null,
      header: t('Account'),
      privilege: Privileges.ViewSettingsTab,
    },
    AccountSettingsLink,
    PartnerSettingsLink,
    {
      key: null,
      header: t('%{resolveTitle} settings', { resolveTitle: getProductFamilyName('gotoresolve') }),
      privilege: Privileges.AppLinks,
    },
    pbx?.showResolve !== undefined ? ResolveDeviceGroupsLink : SkeletonLink,
    pbx?.showResolve !== undefined ? ZeroTrustLink : SkeletonLink,
    pbx?.showResolve !== undefined ? RmmLink : SkeletonLink,
    pbx?.showResolveHelpline !== undefined ? ResolveHelplineLink : SkeletonLink,
    pbx?.showResolveKnowledgebase !== undefined ? ResolveKnowledgebaseLink : SkeletonLink,
    ...(showResolveAILink ? (pbx?.showResolve !== undefined ? [ResolveAILink] : [SkeletonLink]) : []),
  ]

  const billingRoute = [
    SubscriptionsLink,
    QuotesLink,
    InvoiceLink,
    PendingRenewalsLink,
    PaymentMethodLink,
    BillingAddressesLink,
    TaxExemptionLink,
  ]

  const gtcBillingRoute = [GtcInvoiceLink, GtcPaymentOptionsLink, GtcBillingDetailsLink]

  const ResourcesSectionTitle = {
    key: null,
    header: t('Resources'),
    privilege: Privileges.AppLinks,
  }
  const aiEmployeeRoutes = [
    AIRGettingStartedLink,
    AIReceptionistLink,
    ResourcesSectionTitle,
    KnowledgeBaseLink,
    InfoCaptureLink,
  ]

  const buildLinks = useCallback(
    (filters: string[] = []): SidebarLinks => {
      const links = {
        home: homeRoute,
        [PEOPLE_ROUTE]: peopleRoute,
        ['inbox']: inboxRoute,
        [DIGITAL_WORKFORCE_ROUTE]: aiEmployeeRoutes,
        [DEVICES_ROUTE]: deviceRoute,
        [PLACES_ROUTE]: placeRoute,
        [PHONES_ROUTE]: phoneRoute,
        [BILLING_ROUTE]: isAccountOnCommerceSystem ? billingRoute : [],
        [GTC_BILLING_ROUTE]: gtcBillingRoute,
        [SETTINGS_ROUTE]: settingRoute,
      }

      return Object.keys(links).reduce((cumulativeLinks: SidebarLinks, section: string) => {
        const filteredLinks = links[section]
          .filter((l) => !filters.includes(l.key) && isPrivilegeGranted(l.privilege, securityContext))
          .filter((l, i, currentFilteredLinks) => !!l.key || !!currentFilteredLinks[i + 1]?.key)

        return {
          ...cumulativeLinks,
          [section]: filteredLinks,
        }
      }, {})
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pbx, connectedUser, securityContext, userContext],
  )

  const filterProbeManagement = (): string[] =>
    productsSettings?.jive?.networkMeasurementsAllowed ? [] : [NetworkLink.key!]
  const filterVirtualReceptionist = (): string[] =>
    productsSettings?.jive?.virtualReceptionistAllowed || virtualReceptionist
      ? []
      : [AIReceptionistLink.key!, KnowledgeBaseLink.key!, InfoCaptureLink.key!, AIRGettingStartedLink.key!]
  const filterAssistLink = (): string[] => (pbx?.showAssist ? [] : [AssistLink.key!])
  const filterVirtualFaxLink = (): string[] => (pbx?.showVirtualFax ? [] : [VirtualFaxLink.key!])
  const filterRooms = (): string[] => (pbx?.showRooms ? [] : [RoomsSettingsLink.key!])
  const filterResolve = (): string[] => (pbx?.showResolve ? [] : [ResolveLink.key!, RmmLink.key!])
  const filterCallReports = (): string[] => (hasCallReportApp ? [] : [CallReportsLink.key!])

  const filterResolveDeviceGroups = (): string[] =>
    pbx?.showResolve && userHasLicense('GOTORESOLVE') && !userHasLicense('GOTORESOLVESUPPORT')
      ? []
      : [ResolveDeviceGroupsLink.key!]

  const filterResolveZeroTrust = (): string[] =>
    pbx?.showResolve && !userHasLicense('GOTORESOLVESUPPORT') ? [] : [ZeroTrustLink.key!]
  const filterTraining = (): string[] => (pbx?.showTraining ? [] : [TrainingSettingsLink.key!])
  const filterWebinar = (): string[] => (pbx?.showWebinar ? [] : [WebinarSettingsLink.key!])
  const filterMeetings = (): string[] => (pbx?.showMeetings ? [] : [MeetingsSettingsLink.key!])
  const filterHelpdesk = (): string[] => (pbx?.showHelpdesk ? [] : [HelpdeskLink.key!])
  const filterResolveHelpline = (): string[] => (pbx?.showResolveHelpline ? [] : [ResolveHelplineLink.key!])
  const filterResolveKnowledgebase = (): string[] =>
    pbx?.showResolveKnowledgebase ? [] : [ResolveKnowledgebaseLink.key!]
  const filterBillingAdminUsers = (): string[] =>
    isBillingAdmin || partnerHasChildBillingAccess
      ? []
      : [SubscriptionsLink.key!, InvoiceLink.key!, BillingAddressesLink.key!]
  const filterPendingRenewals = (): string[] => (showPendingRenewal ? [] : [PendingRenewalsLink.key!])
  const filterTaxExemptions = (): string[] => (showTaxExemption ? [] : [TaxExemptionLink.key!])
  const filterPaymentMethod = (): string[] => (showPaymentMethod ? [] : [PaymentMethodLink.key!])
  const filterQuotes = (): string[] => (showQuotes ? [] : [QuotesLink.key!])
  const filterGtcBillingViewAccess = (): string[] =>
    isGtcBillingAccount && canViewGtcBilling
      ? []
      : [GtcInvoiceLink.key!, GtcPaymentOptionsLink.key!, GtcBillingDetailsLink.key!]
  const filterGtcBillingPayAccess = (): string[] =>
    isGtcBillingAccount && canPayInGtcBilling ? [] : [GtcPaymentOptionsLink.key!, GtcBillingDetailsLink.key!]
  const filterGtcBillingViewBillingDetailsAccess = (): string[] =>
    isGtcBillingAccount && canViewBillingDetailsInGtcBilling ? [] : [GtcBillingDetailsLink.key!]
  const filterSystemUsers = (): string[] =>
    systemUserHasBillingAccess ? [SubscriptionsLink.key!, InvoiceLink.key!, BillingAddressesLink.key!] : []
  const filterPartnerSettings = (): string[] => (showPartnerSettingsLink ? [] : [PartnerSettingsLink.key!])

  const filterByOrganization = (): string[] =>
    pbx?.showJive
      ? []
      : [
          AudioLibraryLink.key!,
          CallFiltersLink.key!,
          ...CallQueuesLinks.map((link) => link.key!),
          CallReportsLink.key!,
          ConferenceBridgesLink.key!,
          DashboardLink.key!,
          DevicesLink.key!,
          DialPlansLink.key!,
          DirectoriesLink.key!,
          DirectExtensionsLink.key!,
          ExtensionsLink.key!,
          LocationsLink.key!,
          NetworkAccessLink.key!,
          OrdersLink.key!,
          OutboundProxiesLink.key!,
          PagingLink.key!,
          SmsCampaignLink.key!,
          PhoneNumbersLink.key!,
          PhonesSettingsLink.key!,
          RingGroupsLink.key!,
          SchedulesLink.key!,
          SharedVoicemailLink.key!,
          SipTrunksLink.key!,
          ContactsLink.key!,
          ContactGroupsLink.key!,
          ContactCustomFieldsLink.key!,
        ]

  return buildLinks([
    ...filterAssistLink(),
    ...filterProbeManagement(),
    ...filterVirtualFaxLink(),
    ...filterByOrganization(),
    ...filterRooms(),
    ...filterCallReports(),
    ...filterResolve(),
    ...filterResolveHelpline(),
    ...filterResolveKnowledgebase(),
    ...filterResolveDeviceGroups(),
    ...filterResolveZeroTrust(),
    ...filterHelpdesk(),
    ...filterTraining(),
    ...filterWebinar(),
    ...filterInbox(),
    ...filterMeetings(),
    ...filterGtcBillingViewAccess(),
    ...filterGtcBillingPayAccess(),
    ...filterGtcBillingViewBillingDetailsAccess(),
    ...filterPendingRenewals(),
    ...filterTaxExemptions(),
    ...filterPaymentMethod(),
    ...filterQuotes(),
    ...filterPartnerSettings(),
    ...filterSystemUsers(),
    ...filterBillingAdminUsers(),
    ...filterVirtualReceptionist(),
  ])
}

export default AppLinks
